import React, { FC, ReactNode } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { PillButton, SurfaceCard } from "../ui";

export interface SerialStepFormProps {
  label: string;
  helperText?: ReactNode;
  /** Reference photo showing where the serial is printed. */
  image?: string;
  imageAlt?: string;
  value: string;
  onChange: (value: string) => void;
  /** Enables the submit button. */
  valid: boolean;
  /** Shown when the field has content but is not yet valid. */
  errorText?: string;
  onSubmit: () => void;
  onBack?: () => void;
  children?: ReactNode;
}

/**
 * The shared shape of the two serial-entry steps: a reference photo, one field
 * and a back/next pair.
 */
const SerialStepForm: FC<SerialStepFormProps> = ({
  label,
  helperText,
  image,
  imageAlt,
  value,
  onChange,
  valid,
  errorText,
  onSubmit,
  onBack,
  children,
}) => {
  const showError = Boolean(errorText) && value.trim().length > 0 && !valid;

  return (
    <SurfaceCard>
      <Box
        component="form"
        onSubmit={(event: React.FormEvent) => {
          event.preventDefault();
          if (valid) onSubmit();
        }}
      >
        {image && (
          <Box
            component="img"
            src={image}
            alt={imageAlt ?? ""}
            sx={{
              width: "100%",
              maxHeight: 240,
              objectFit: "contain",
              borderRadius: 1,
              mb: 3,
              bgcolor: "background.default",
            }}
          />
        )}

        <Typography variant="h4" sx={{ mb: 1 }}>
          {label}
        </Typography>

        {helperText && (
          <Typography variant="body2" sx={{ mb: 2.5 }}>
            {helperText}
          </Typography>
        )}

        <TextField
          label={label}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          fullWidth
          autoFocus
          autoComplete="off"
          spellCheck={false}
          error={showError}
          helperText={showError ? errorText : " "}
          inputProps={{ "aria-label": label }}
        />

        {children}

        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
            mt: 2,
          }}
        >
          {onBack && (
            <PillButton tone="outline" onClick={onBack} type="button">
              Back
            </PillButton>
          )}
          <PillButton tone="amber" type="submit" disabled={!valid} withArrow>
            Continue
          </PillButton>
        </Box>
      </Box>
    </SurfaceCard>
  );
};

export default SerialStepForm;
